import createAuth0Client from "@auth0/auth0-spa-js";
import PropTypes from "prop-types";
import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useHistory } from "react-router-dom";
import { get, has } from "lodash";

import { parseQueryString } from "src/util/parseQueryString";

import { clearSupportImpersonationBeforeLogout } from "src/services/clearSupportImpersonationOnLogout";
import { completeLogout, getImpersonatedUser } from "src/services/auth";
import { shouldIgnoreError } from "src/util/url";

export const Auth0Context = React.createContext();
export const useAuth0 = () => useContext(Auth0Context);
export const Auth0Provider = ({
  children,
  isGenericDomain,
  ...initOptions
}) => {
  const history = useHistory();
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [auth0ErrorContext, setAuth0ErrorContext] = useState();
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [auth0Client, setAuth0] = useState();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    const initAuth0 = async () => {
      const urlParams = parseQueryString(window.location.search);

      const screenHint = get(urlParams, "screenHint");

      const auth0FromHook = await createAuth0Client({
        ...initOptions,
        ...(screenHint && { screen_hint: screenHint }),
      });

      setAuth0(auth0FromHook);

      if (has(urlParams, "error") && !shouldIgnoreError()) {
        // https://auth0.com/docs/libraries/error-messages
        setIsAccessDenied(true);
        setAuth0ErrorContext({
          source: "auth0",
          error: get(urlParams, "error"),
          errorDescription: get(urlParams, "error_description"),
        });
      } else if (
        has(urlParams, "code") &&
        has(urlParams, "state") &&
        window.location.pathname === "/"
      ) {
        // Auth0 redirect_uri is window.location.origin (root only). Third-party
        // OAuth callbacks (e.g. BambooHR) also use code+state on other paths.
        try {
          const { appState } = await auth0FromHook.handleRedirectCallback();
          const redirectUrl = appState?.targetUrl || window.location.pathname;
          history.push(redirectUrl);
        } catch (error) {
          // in case there is an error in handleRedirectCallback, redirect the user to home page so the login flow can complete or can be restarted
          console.log(error);
          history.push("/");
        }
      }

      const isAuth = await auth0FromHook.isAuthenticated();
      setIsAuthenticated(isAuth);

      if (isAuth) {
        const auth0User = await auth0FromHook.getUser();
        setUser(getImpersonatedUser(auth0User));
      }
      setLoading(false);
    };
    initAuth0();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginWithPopup = useCallback(
    async (params = {}) => {
      setPopupOpen(true);
      try {
        await auth0Client.loginWithPopup(params);
      } catch (error) {
        console.error(error);
      } finally {
        setPopupOpen(false);
      }

      setUser(getImpersonatedUser(await auth0Client.getUser()));
      setIsAuthenticated(true);
    },
    [auth0Client]
  );

  const handleRedirectCallback = useCallback(async () => {
    setLoading(true);
    await auth0Client.handleRedirectCallback();
    const theUser = await auth0Client.getUser();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(getImpersonatedUser(theUser));
  }, [auth0Client]);

  const handleLogout = useCallback(
    async (...p) => {
      if (auth0Client) {
        try {
          await clearSupportImpersonationBeforeLogout(auth0Client);
        } catch (e) {
          console.error(
            "[auth0Provider] clearSupportImpersonationBeforeLogout failed:",
            e
          );
        }
      }
      completeLogout();
      // default redirect to originating request URL's logout page
      return auth0Client.logout({
        returnTo: `${window.location.origin}/logout`,
        ...p,
      });
    },
    [auth0Client]
  );

  const getIdTokenClaims = useCallback(
    (...p) => auth0Client.getIdTokenClaims(...p),
    [auth0Client]
  );
  const loginWithRedirect = useCallback(
    (...p) => auth0Client.loginWithRedirect(...p),
    [auth0Client]
  );
  const getTokenSilently = useCallback(
    (...p) => auth0Client.getTokenSilently(...p),
    [auth0Client]
  );
  const getTokenWithPopup = useCallback(
    (...p) => auth0Client.getTokenWithPopup(...p),
    [auth0Client]
  );

  const contextValue = useMemo(
    () => ({
      auth0ErrorContext,
      isAuthenticated,
      isAccessDenied,
      isGenericDomain,
      user,
      loading,
      popupOpen,
      loginWithPopup,
      handleRedirectCallback,
      getIdTokenClaims,
      loginWithRedirect,
      getTokenSilently,
      getTokenWithPopup,
      logout: handleLogout,
    }),
    [
      auth0ErrorContext,
      isAuthenticated,
      isAccessDenied,
      isGenericDomain,
      user,
      loading,
      popupOpen,
      loginWithPopup,
      handleRedirectCallback,
      getIdTokenClaims,
      loginWithRedirect,
      getTokenSilently,
      getTokenWithPopup,
      handleLogout,
    ]
  );

  return (
    <Auth0Context.Provider value={contextValue}>
      {children}
    </Auth0Context.Provider>
  );
};

Auth0Provider.propTypes = {
  children: PropTypes.node,
  isGenericDomain: PropTypes.bool,
  initOptions: PropTypes.object,
};
